import { defineWidget } from '../../widget-types';
import type { BlockNode, BlockGroupConfig } from '../../block-types';

/**
 * Default blocks for the Exogro header — decomposed into 3 rows.
 * Each block has an explicit `layout.groupId` so the centralized
 * GenericGroupedBlockLayout can render them without ID-prefix fallback.
 */
const exogroHeaderDefaultBlocks: BlockNode[] = [
  // ── Row 1: Announcement Bar ──
  {
    id: 'announcement-text',
    type: 'text',
    content: {
      type: 'text',
      value: 'ห้ามพลาด!! ส่วนลด 10% เมื่อซื้อครบ 2,000 บาท ขึ้นไป',
      fontSize: 14,
      color: '#FFFFFF',
      textAlign: 'center',
    },
    layout: { order: 0, widthPct: 100, groupId: 'announcement' },
  },

  // ── Row 2: Top Bar ──
  {
    id: 'topbar-logo',
    type: 'image',
    content: {
      type: 'image',
      src: '/exogro-logo.png',
      alt: 'Exogro Logo',
      objectFit: 'contain',
    },
    layout: { order: 0, widthPct: 25, heightPx: 40, groupId: 'topbar' },
  },
  {
    id: 'topbar-search',
    type: 'search-bar',
    content: {
      type: 'search-bar',
      placeholder: 'ค้นหาสินค้า',
    },
    layout: { order: 1, widthPct: 50, groupId: 'topbar' },
  },
  {
    id: 'topbar-line-icon',
    type: 'icon',
    content: {
      type: 'icon',
      name: 'line-circle',
      size: 40,
      color: '#06C755',
    },
    layout: { order: 2, widthPct: 25, groupId: 'topbar' },
  },
  {
    id: 'topbar-line-text',
    type: 'text',
    content: {
      type: 'text',
      value: 'ติดต่อทีมงานแอด LINE\nLine ID : @exogro',
      fontSize: 12,
      color: '#374151',
    },
    layout: { order: 3, widthPct: 25, groupId: 'topbar' },
  },

  // ── Row 3: Navigation Bar ──
  {
    id: 'navbar-categories',
    type: 'category-nav',
    content: {
      type: 'category-nav',
      buttonLabel: 'หมวดหมู่สินค้า',
      brandColor: '#FF6600',
    },
    layout: { order: 0, widthPct: 25, groupId: 'navbar' },
  },
  {
    id: 'navbar-link-products',
    type: 'button',
    content: {
      type: 'button',
      label: 'สินค้า',
      href: '/search',
      variant: 'ghost',
      size: 'sm',
    },
    layout: { order: 1, widthPct: 25, groupId: 'navbar' },
  },
  {
    id: 'navbar-link-why',
    type: 'button',
    content: {
      type: 'button',
      label: 'ทำไมต้องเลือก Exogro',
      href: '/pages/about',
      variant: 'ghost',
      size: 'sm',
    },
    layout: { order: 2, widthPct: 25, groupId: 'navbar' },
  },
  {
    id: 'navbar-link-brands',
    type: 'button',
    content: {
      type: 'button',
      label: 'แบรนด์',
      href: '/search',
      variant: 'ghost',
      size: 'sm',
    },
    layout: { order: 3, widthPct: 25, groupId: 'navbar' },
  },
  {
    id: 'navbar-link-articles',
    type: 'button',
    content: {
      type: 'button',
      label: 'บทความ',
      href: '/search',
      variant: 'ghost',
      size: 'sm',
    },
    layout: { order: 4, widthPct: 25, groupId: 'navbar' },
  },
  {
    id: 'navbar-link-about',
    type: 'button',
    content: {
      type: 'button',
      label: 'เกี่ยวกับเรา',
      href: '/pages/about',
      variant: 'ghost',
      size: 'sm',
    },
    layout: { order: 5, widthPct: 25, groupId: 'navbar' },
  },
  {
    id: 'navbar-spacer',
    type: 'spacer',
    content: {
      type: 'spacer',
      heightPx: 0,
    },
    layout: { order: 6, widthPct: 100, groupId: 'navbar' },
  },
  {
    id: 'navbar-language',
    type: 'text',
    content: {
      type: 'text',
      value: 'ภาษาไทย',
      fontSize: 14,
      color: '#4B5563',
    },
    layout: { order: 7, widthPct: 25, groupId: 'navbar' },
  },
  {
    id: 'navbar-account',
    type: 'icon',
    content: {
      type: 'icon',
      name: 'user',
      size: 20,
      color: '#374151',
      behavior: 'account',
    },
    layout: { order: 8, widthPct: 25, groupId: 'navbar' },
  },
  {
    id: 'navbar-favorites',
    type: 'icon',
    content: {
      type: 'icon',
      name: 'heart',
      size: 20,
      color: '#374151',
      behavior: 'favorites',
    },
    layout: { order: 9, widthPct: 25, groupId: 'navbar' },
  },
  {
    id: 'navbar-cart',
    type: 'icon',
    content: {
      type: 'icon',
      name: 'shopping-bag',
      size: 20,
      color: '#374151',
      behavior: 'cart',
    },
    layout: { order: 10, widthPct: 25, groupId: 'navbar' },
  },
];

// ═══════════════════════════════════════════════════════════════════════════
// BLOCK GROUP CONFIG (declarative layout for GenericGroupedBlockLayout)
// ═══════════════════════════════════════════════════════════════════════════

const exogroHeaderBlockGroups: BlockGroupConfig[] = [
  {
    id: 'announcement',
    label: 'Announcement Bar',
    direction: 'row',
    containerClass: 'bg-black py-2 text-center',
    allowedTypes: ['text'],
    naturalSizing: true,
  },
  {
    id: 'topbar',
    label: 'Top Bar',
    direction: 'row',
    containerClass: 'border-b bg-white',
    innerClass: 'mx-auto flex max-w-7xl items-center gap-6 px-4 py-3',
    allowedTypes: ['text', 'image', 'search-bar', 'icon', 'spacer'],
    naturalSizing: true,
  },
  {
    id: 'navbar',
    label: 'Navigation Bar',
    direction: 'row',
    containerClass: 'border-b bg-white',
    innerClass: 'mx-auto flex max-w-7xl items-center gap-1 px-4 py-2',
    allowedTypes: ['button', 'category-nav', 'icon', 'text', 'spacer'],
    naturalSizing: true,
  },
];

export const exogroHeaderWidget = defineWidget({
  type: 'code:exogro-header',
  schema: {
    label: 'Exogro Header',
    description: 'Industrial supply header with announcement bar, search, and category navigation',
    props: {
      announcementText: {
        type: 'string',
        label: 'Announcement Text',
        default: 'ห้ามพลาด!! ส่วนลด 10% เมื่อซื้อครบ 2,000 บาท ขึ้นไป',
      },
      logoUrl: {
        type: 'image',
        label: 'Logo',
        default: '/exogro-logo.png',
      },
      brandColor: {
        type: 'color',
        label: 'Brand Color',
        default: '#FF6600',
      },
      lineId: {
        type: 'string',
        label: 'LINE ID',
        default: '@exogro',
      },
      categoryButtonLabel: {
        type: 'string',
        label: 'Category Button Label',
        default: 'หมวดหมู่สินค้า',
      },
    },
    slots: {},
    dataDeps: ['categories'],
  },
  nameTh: 'เฮดเดอร์ Exogro',
  descriptionTh: 'เฮดเดอร์สำหรับร้านค้าอุตสาหกรรม พร้อมแถบประกาศ ค้นหา และหมวดหมู่',
  category: 'templates',
  thumbnail: '/widget-thumbnails/code/exogro-header.png',
  defaults: {
    type: 'code:exogro-header',
    props: {
      announcementText: 'ห้ามพลาด!! ส่วนลด 10% เมื่อซื้อครบ 2,000 บาท ขึ้นไป',
      logoUrl: '/exogro-logo.png',
      brandColor: '#FF6600',
      lineId: '@exogro',
      categoryButtonLabel: 'หมวดหมู่สินค้า',
    },
  },
  defaultBlocks: exogroHeaderDefaultBlocks,
  blockGroups: exogroHeaderBlockGroups,
});
